import { Loader2, Send } from 'lucide-react';
import type {
  KnowledgeChatMessage,
  KnowledgePendingMutation,
} from '@/entities/knowledge';
import { Button } from '@/shared/ui/shadcn/button';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { cn } from '@/shared/lib/utils';

export function KnowledgeChatPanel({
  messages,
  pending,
  draft,
  busy,
  onDraftChange,
  onSend,
  onConfirm,
  onReject,
}: {
  messages: KnowledgeChatMessage[];
  pending: KnowledgePendingMutation[];
  draft: string;
  busy: boolean;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onConfirm: () => void;
  onReject: () => void;
}) {
  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-background">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Ask about entities, propose edits, then confirm the plan before anything is written.
          </p>
        ) : null}
        {messages.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            className={cn(
              'rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
              message.role === 'user'
                ? 'ml-6 bg-primary text-primary-foreground'
                : 'mr-6 bg-muted text-foreground',
            )}
          >
            {message.content}
          </div>
        ))}

        {pending.length > 0 ? (
          <div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
            <div className="text-sm font-medium">Proposed changes</div>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              {pending.map((mutation, index) => (
                <li key={`${mutation.action}-${index}`} className="rounded border bg-background/80 p-2">
                  <div className="font-medium text-foreground">
                    {mutation.action}
                    {mutation.label ? ` · ${mutation.label}` : ''}
                    {mutation.id ? `/${mutation.id}` : ''}
                  </div>
                  <div>{mutation.summary}</div>
                  {mutation.edge_type ? (
                    <div>
                      {mutation.from_label}/{mutation.from_id} -[{mutation.edge_type}]→{' '}
                      {mutation.to_label}/{mutation.to_id}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
            <div className="flex gap-2 pt-1">
              <Button type="button" size="sm" onClick={onConfirm} disabled={busy}>
                Confirm
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={onReject} disabled={busy}>
                Reject
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="border-t p-3">
        <div className="flex gap-2">
          <Textarea
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder="Ask or request a knowledge change…"
            className="min-h-[72px] resize-none"
            disabled={busy}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                onSend();
              }
            }}
          />
          <Button type="button" size="icon" onClick={onSend} disabled={busy || !draft.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
